import React, { useRef, useEffect, useMemo } from "react";
import { Container, Sprite } from "@pixi/react";
import ReelBackController, {
  STATIC_CONFIG,
  SPINE_CONFIG,
} from "./ReelBackController";
import { Settings } from "../../../settings";
import { safeInit } from "../../../utilities";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";

const ReelBack = ({
  symbolWidth,
  symbolHeight,
  dimensions,
  dividerDimensions,
  renderMode = "spine",
}) => {
  const reelBackControllerRef = useRef(null);
  const containerRef = useRef(null);


  useEffect(() => {
    if (
      renderMode === "spine" &&
      containerRef.current &&
      !reelBackControllerRef.current
    ) {
      safeInit(() => {
        reelBackControllerRef.current = new ReelBackController(
          containerRef.current,
          symbolWidth,
          symbolHeight,
          dimensions,
          dividerDimensions,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode]);

  // Handle dimension changes for responsive design (spine mode only)
  useEffect(() => {
    if (renderMode === "spine" && reelBackControllerRef.current) {
      reelBackControllerRef.current.resizeReelBack(
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight, renderMode]);

  // Memoize divider count - constant value
  const dividerCount = useMemo(() => Settings.REEL_COUNT - 1, []);

  // Always use base config (feature config removed — H&W disabled)
  const config = STATIC_CONFIG;

  // Memoize divider width
  const dividerWidth = useMemo(
    () => dividerDimensions?.width ?? 0,
    [dividerDimensions?.width],
  );

  // Memoize static dimensions calculations
  const staticWidth = useMemo(
    () =>
      (symbolWidth * Settings.REEL_COUNT + dividerWidth * dividerCount) *
      config.widthMultiplier,
    [symbolWidth, dividerWidth, dividerCount, config.widthMultiplier],
  );

  const staticHeight = useMemo(
    () => symbolHeight * Settings.SYMBOLS_VISIBLE * config.heightMultiplier,
    [symbolHeight, config.heightMultiplier],
  );

  // Memoize texture lookup for static mode
  const staticTexture = useMemo(() => {
    if (renderMode !== "static") return null;
    return PIXI.Assets.get(ASSET_NAME.reel_frame_back_static);
  }, [renderMode]);

  // Memoize container position calculations
  const containerPosition = useMemo(() => {
    if (renderMode === "spine") {
      return {
        x: symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.xOffset,
        y:
          -symbolHeight *
          Settings.SYMBOLS_VISIBLE *
          SPINE_CONFIG.yOffset,
      };
    }
    return {
      x: config.xOffset,
      y: config.yOffset,
    };
  }, [
    renderMode,
    symbolWidth,
    symbolHeight,
    config.xOffset,
    config.yOffset,
  ]);

  return (
    <Container
      y={containerPosition.y}
      x={containerPosition.x}
      ref={containerRef}
      name="back_texture"
      zIndex={1}
    >
      {renderMode === "static" && staticTexture && (
        <Sprite
          texture={staticTexture}
          width={staticWidth}
          height={staticHeight}
          zIndex={2}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default ReelBack;
